import React, { useState, useRef, useEffect } from "react";

interface DropdownOption<T> {
  label: string;
  value: T;
}

interface DropdownProps<T> {
  options: DropdownOption<T>[];
  value: T | null;
  onChange: (value: T) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}


export function Dropdown<T>({
  options,
  value,
  onChange,
  placeholder = "Select...",
  disabled = false,
  className = "",
}: DropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Keyboard navigation (optional enhancement)
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);

  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (!open && (event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      if (!disabled) setOpen(true);
      return;
    }
    if (open) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setHighlightedIndex((prev) =>
          prev < options.length - 1 ? prev + 1 : 0
        );
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : options.length - 1
        );
      } else if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < options.length) {
          onChange(options[highlightedIndex].value);
          setOpen(false);
          setHighlightedIndex(-1);
        }
      } else if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
        setHighlightedIndex(-1);
      }
    }
  }

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <div
      ref={dropdownRef}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={onKeyDown}
      className={`relative inline-block text-left w-64 ${className} ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
        }`}
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-disabled={disabled}
    >
      <div
        onClick={() => !disabled && setOpen((o) => !o)}
        className="inline-flex justify-between items-center w-full rounded border border-borderColor bg-cardBackground px-4 py-2 text-bodyText shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
      >
        <span className={`${selectedOption ? "text-headingText" : "text-bodySecondary"}`}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <svg
          className={`ml-2 h-5 w-5 text-bodySecondary transition-transform duration-200 ${open ? "transform rotate-180" : ""
            }`}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-cardBackground border border-borderColor rounded shadow-lg">
          {/* Search input */}
          <div className="p-2">
            <input
              ref={searchRef}
              type="search"
              autoFocus
              value={search}
              placeholder="Search..."
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded border border-gray-300 px-3 py-2 shadow-sm focus:ring-2 focus:ring-primary focus:outline-none"
              aria-label="Search inside dropdown"
            />
          </div>
          <ul
            role="listbox"
            tabIndex={-1}
            className="absolute z-50 mt-1 w-full bg-cardBackground border border-borderColor rounded shadow-lg max-h-60 overflow-auto focus:outline-none"
          >
            {options.filter((option) =>
              option.label.toLowerCase().includes(search.toLowerCase())
            )
              .map((option, idx) => {
                const selected = value === option.value;
                const highlighted = idx === highlightedIndex;
                return (
                  <li
                    key={option.value as any}
                    role="option"
                    aria-selected={selected}
                    className={`cursor-pointer select-none relative py-2 pl-3 pr-9 ${selected ? "bg-primary text-cardBackground font-semibold" : "text-bodyText"
                      } ${highlighted && !selected ? "bg-light_primary" : ""}`}
                    onClick={() => {
                      onChange(option.value);
                      setOpen(false);
                      setHighlightedIndex(-1);
                    }}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                  >
                    <span className={`block truncate ${selected ? "font-semibold" : ""}`}>
                      {option.label}
                    </span>

                    {selected && (
                      <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-cardBackground">
                        {/* Check Icon */}
                        <svg
                          className="h-5 w-5"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          aria-hidden="true"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                    )}
                  </li>
                );
              })}
          </ul>
        </div>
      )}
    </div>
  );
}
